/**
 * Renovation cost calculator — H&K ReMööbel
 *
 * Plain client-side (JS) pricing logic, no server involved. All prices are
 * structured ESTIMATES that the business owner must review and adjust
 * before publishing — see the "Category consolidation and pricing logic"
 * section of the plan for the derivation logic.
 *
 * Derivation rule: each category's cheapest work type (New upholstery
 * fabric / Sanding & refinishing) price range ≈ the retail (new furniture)
 * low price; "Full restoration"'s high price ≈ the retail high price.
 * Work types in between are interpolated.
 *
 * ✓ = derived directly from the given examples, ⚠ = interpolated/estimated
 * (details in /plans/i-need-to-create-elegant-spindle.md)
 *
 * All displayed TEXT (category/work-type/material names, labels, summary
 * format) comes from Shopify's translation system (Translate & Adapt), not
 * from this file — this is a static asset and Liquid never processes it.
 * The section (renovation-calculator.liquid) writes the translated strings
 * onto the page as a <script type="application/json" data-rc-i18n> block;
 * this file reads that in and fills the structures below (prices,
 * multipliers, keys) with the matching labels. If the i18n block can't be
 * found for some reason (e.g. this file runs in a test without the
 * section), the Estonian fallback strings below are used so nothing is
 * left blank.
 */
(function () {
  'use strict';

  function readI18n() {
    var el = document.querySelector('[data-rc-i18n]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  var I18N = readI18n() || {};

  function t(value, fallback) {
    return value === undefined || value === null || value === '' ? fallback : value;
  }

  var RC_PRICING = {
    branches: {
      pehme: {
        label: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.label, 'Pehme mööbel'),
        workTypeOrder: ['polster', 'vaht', 'vedrud', 'puitraam', 'restaureerimine'],
        workTypeLabels: {
          polster: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.workTypes && I18N.branches.pehme.workTypes.polster, 'Uus kattematerjal'),
          vaht: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.workTypes && I18N.branches.pehme.workTypes.vaht, 'Uus poroloon'),
          vedrud: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.workTypes && I18N.branches.pehme.workTypes.vedrud, 'Vedrude parandus'),
          puitraam: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.workTypes && I18N.branches.pehme.workTypes.puitraam, 'Puitraami parandus'),
          restaureerimine: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.workTypes && I18N.branches.pehme.workTypes.restaureerimine, 'Täielik restaureerimine')
        },
        materialOrder: ['kangas', 'tehisnahk', 'paris_nahk', 'segamaterjal'],
        materialLabels: {
          kangas: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.materials && I18N.branches.pehme.materials.kangas, 'Kangas'),
          tehisnahk: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.materials && I18N.branches.pehme.materials.tehisnahk, 'Tehisnahk'),
          paris_nahk: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.materials && I18N.branches.pehme.materials.paris_nahk, 'Päris nahk'),
          segamaterjal: t(I18N.branches && I18N.branches.pehme && I18N.branches.pehme.materials && I18N.branches.pehme.materials.segamaterjal, 'Segamaterjal')
        },
        materialMult: { kangas: 1.0, tehisnahk: 1.10, paris_nahk: 1.20, segamaterjal: 1.10 }
      },
      puit: {
        label: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.label, 'Puitmööbel'),
        workTypeOrder: ['lihvimine', 'varv', 'konstruktsioon', 'spoon', 'restaureerimine'],
        workTypeLabels: {
          lihvimine: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.workTypes && I18N.branches.puit.workTypes.lihvimine, 'Lihvimine ja viimistlus'),
          varv: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.workTypes && I18N.branches.puit.workTypes.varv, 'Värvimine uue tooniga'),
          konstruktsioon: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.workTypes && I18N.branches.puit.workTypes.konstruktsioon, 'Konstruktsiooniline parandus'),
          spoon: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.workTypes && I18N.branches.puit.workTypes.spoon, 'Spooni parandus'),
          restaureerimine: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.workTypes && I18N.branches.puit.workTypes.restaureerimine, 'Täielik restaureerimine')
        },
        materialOrder: ['puit', 'segamaterjal'],
        materialLabels: {
          puit: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.materials && I18N.branches.puit.materials.puit, 'Puit'),
          segamaterjal: t(I18N.branches && I18N.branches.puit && I18N.branches.puit.materials && I18N.branches.puit.materials.segamaterjal, 'Segamaterjal')
        },
        materialMult: { puit: 1.0, segamaterjal: 1.10 }
      }
    },

    // Prices and size multipliers are business rules, not text — they stay
    // here directly. Only "label"/"hint" come from the i18n block (I18N.categories).
    categories: [
      {
        key: 'toolid', branch: 'pehme',
        label: t(I18N.categories && I18N.categories.toolid && I18N.categories.toolid.label, 'Toolid'),
        hint: t(I18N.categories && I18N.categories.toolid && I18N.categories.toolid.hint, 'söögitool'),
        sizes: { vaike: 0.85, keskmine: 1.0, suur: 1.4 },
        prices: { polster: [55, 95], vaht: [45, 75], vedrud: [45, 80], puitraam: [55, 95], restaureerimine: [180, 300] }
      },
      {
        key: 'tugitool', branch: 'pehme',
        label: t(I18N.categories && I18N.categories.tugitool && I18N.categories.tugitool.label, 'Tugitoolid'),
        hint: t(I18N.categories && I18N.categories.tugitool && I18N.categories.tugitool.hint, ''),
        sizes: { vaike: 0.85, keskmine: 1.0, suur: 1.3 },
        prices: { polster: [300, 500], vaht: [200, 350], vedrud: [180, 300], puitraam: [180, 320], restaureerimine: [650, 1050] }
      },
      {
        key: 'diivan', branch: 'pehme',
        label: t(I18N.categories && I18N.categories.diivan && I18N.categories.diivan.label, 'Diivanid'),
        hint: t(I18N.categories && I18N.categories.diivan && I18N.categories.diivan.hint, '2- ja 3-kohalised'),
        sizes: { vaike: 0.85, keskmine: 1.0, suur: 1.3 },
        prices: { polster: [900, 1500], vaht: [650, 1100], vedrud: [550, 900], puitraam: [550, 950], restaureerimine: [1600, 2500] }
      },
      {
        key: 'nurgadiivan', branch: 'pehme',
        label: t(I18N.categories && I18N.categories.nurgadiivan && I18N.categories.nurgadiivan.label, 'Nurgadiivanid'),
        hint: t(I18N.categories && I18N.categories.nurgadiivan && I18N.categories.nurgadiivan.hint, ''),
        sizes: { vaike: 0.85, keskmine: 1.0, suur: 1.35 },
        prices: { polster: [1400, 2300], vaht: [950, 1700], vedrud: [750, 1300], puitraam: [800, 1400], restaureerimine: [2400, 3800] }
      },
      {
        key: 'vaiksed_pehmed', branch: 'pehme',
        label: t(I18N.categories && I18N.categories.vaiksed_pehmed && I18N.categories.vaiksed_pehmed.label, 'Väikesed pehmed esemed'),
        hint: t(I18N.categories && I18N.categories.vaiksed_pehmed && I18N.categories.vaiksed_pehmed.hint, 'tumba, järi, voodipeats'),
        sizes: { vaike: 0.85, keskmine: 1.0, suur: 1.3 },
        prices: { polster: [70, 140], vaht: [55, 105], vedrud: [40, 75], puitraam: [45, 85], restaureerimine: [160, 320] }
      },
      {
        key: 'lauad', branch: 'puit',
        label: t(I18N.categories && I18N.categories.lauad && I18N.categories.lauad.label, 'Lauad'),
        hint: t(I18N.categories && I18N.categories.lauad && I18N.categories.lauad.hint, 'söögi-, diivani-, kirjutuslauad'),
        sizes: { vaike: 0.75, keskmine: 1.0, suur: 1.25 },
        prices: { lihvimine: [200, 400], varv: [350, 650], konstruktsioon: [450, 900], spoon: [400, 750], restaureerimine: [650, 1200] }
      },
      {
        key: 'kummutid', branch: 'puit',
        label: t(I18N.categories && I18N.categories.kummutid && I18N.categories.kummutid.label, 'Kummutid ja väiksemad kapid'),
        hint: t(I18N.categories && I18N.categories.kummutid && I18N.categories.kummutid.hint, 'kummut, öökapp, TV-alus, raamaturiiul'),
        sizes: { vaike: 0.70, keskmine: 1.0, suur: 1.3 },
        prices: { lihvimine: [350, 650], varv: [500, 900], konstruktsioon: [450, 850], spoon: [500, 950], restaureerimine: [850, 1500] }
      },
      {
        key: 'suured_kapid', branch: 'puit',
        label: t(I18N.categories && I18N.categories.suured_kapid && I18N.categories.suured_kapid.label, 'Suured kapid'),
        hint: t(I18N.categories && I18N.categories.suured_kapid && I18N.categories.suured_kapid.hint, 'riide-, vitriin-, puhvetkapid'),
        sizes: { vaike: 0.80, keskmine: 1.0, suur: 1.4 },
        prices: { lihvimine: [600, 1100], varv: [800, 1500], konstruktsioon: [700, 1400], spoon: [900, 1800], restaureerimine: [1200, 2500] }
      },
      {
        key: 'voodiraamid', branch: 'puit',
        label: t(I18N.categories && I18N.categories.voodiraamid && I18N.categories.voodiraamid.label, 'Voodiraamid'),
        hint: t(I18N.categories && I18N.categories.voodiraamid && I18N.categories.voodiraamid.hint, ''),
        sizes: { vaike: 0.8, keskmine: 1.0, suur: 1.3 },
        prices: { lihvimine: [350, 650], varv: [500, 950], konstruktsioon: [550, 1000], spoon: [450, 850], restaureerimine: [800, 1500] }
      }
    ]
  };

  var RC_TRANSPORT_FEE = {
    vaike: { yks_suund: 50, edasi_tagasi: 100 },
    keskmine: { yks_suund: 85, edasi_tagasi: 170 },
    suur: { yks_suund: 95, edasi_tagasi: 190 }
  };
  var RC_SIZE_LABELS = {
    vaike: t(I18N.sizes && I18N.sizes.vaike, 'Väike'),
    keskmine: t(I18N.sizes && I18N.sizes.keskmine, 'Keskmine'),
    suur: t(I18N.sizes && I18N.sizes.suur, 'Suur')
  };
  var RC_TRANSPORT_LABELS = {
    ei: t(I18N.transport && I18N.transport.ei, 'Ei'),
    yks_suund: t(I18N.transport && I18N.transport.yks_suund, 'Jah, üks suund'),
    edasi_tagasi: t(I18N.transport && I18N.transport.edasi_tagasi, 'Jah, edasi-tagasi')
  };
  var RC_SUMMARY_STRINGS = {
    marker: t(I18N.summary && I18N.summary.marker, '--- Automaatne hinnakalkulaator ---'),
    categoryLabel: t(I18N.summary && I18N.summary.categoryLabel, 'Mööbligrupp:'),
    workLabel: t(I18N.summary && I18N.summary.workLabel, 'Töö:'),
    sizeLabel: t(I18N.summary && I18N.summary.sizeLabel, 'Suurus:'),
    materialLabel: t(I18N.summary && I18N.summary.materialLabel, 'Materjal:'),
    transportLabel: t(I18N.summary && I18N.summary.transportLabel, 'Transport:'),
    priceLabel: t(I18N.summary && I18N.summary.priceLabel, 'Hinnanguline maksumus:'),
    divider: t(I18N.summary && I18N.summary.divider, '-----------------------------------'),
    extraInfoLabel: t(I18N.summary && I18N.summary.extraInfoLabel, 'Lisainfo minu esemest:')
  };

  // Safety cap: the product of the size and material multipliers must
  // never exceed this value, to prevent unreasonable price stacking on
  // extreme combinations (large + leather + full restoration) — the "Full
  // restoration" tier is already anchored close to the retail (new
  // furniture) high price at medium size, so the cap needs to be more
  // conservative here than for other work types. 1.3 means the largest
  // possible combination lands about 30% above the retail high price —
  // this is a deliberate, freely owner-tunable number, not a fixed rule.
  var RC_MAX_COMBINED_MULT = 1.3;

  function findCategory(key) {
    for (var i = 0; i < RC_PRICING.categories.length; i++) {
      if (RC_PRICING.categories[i].key === key) return RC_PRICING.categories[i];
    }
    return null;
  }

  function roundTo5(n) {
    return Math.round(n / 5) * 5;
  }

  function computeEstimate(selections) {
    var category = findCategory(selections.category);
    if (!category) return null;
    var branch = RC_PRICING.branches[category.branch];
    var range = category.prices[selections.workType];
    if (!range) return null;

    var sizeMult = category.sizes[selections.size] || 1.0;
    var materialMult = (branch.materialMult && branch.materialMult[selections.material]) || 1.0;
    var combinedMult = Math.min(sizeMult * materialMult, RC_MAX_COMBINED_MULT);

    var low = range[0] * combinedMult;
    var high = range[1] * combinedMult;

    var sizeTransportFees = RC_TRANSPORT_FEE[selections.size];
    var transportFee = (sizeTransportFees && sizeTransportFees[selections.transport]) || 0;
    low += transportFee;
    high += transportFee;

    return { low: roundTo5(low), high: roundTo5(high) };
  }

  function formatSummary(selections, estimate) {
    var category = findCategory(selections.category);
    var branch = RC_PRICING.branches[category.branch];
    var workLabel = branch.workTypeLabels[selections.workType] || '';
    var sizeLabel = RC_SIZE_LABELS[selections.size] || '';
    var materialLabel = (branch.materialLabels && branch.materialLabels[selections.material]) || '';
    var transportLabel = RC_TRANSPORT_LABELS[selections.transport] || '';
    var s = RC_SUMMARY_STRINGS;

    return [
      s.marker,
      s.categoryLabel + ' ' + category.label,
      s.workLabel + ' ' + workLabel,
      s.sizeLabel + ' ' + sizeLabel,
      s.materialLabel + ' ' + materialLabel,
      s.transportLabel + ' ' + transportLabel,
      s.priceLabel + ' €' + estimate.low + '–' + estimate.high,
      s.divider,
      '',
      s.extraInfoLabel + ' '
    ].join('\n');
  }

  var STEP_COUNT = 5;

  function initCalculator(root) {
    var state = {
      step: 1,
      branch: null,
      category: null,
      workType: null,
      size: null,
      material: null,
      transport: null
    };

    var progressEls = root.querySelectorAll('[data-step-indicator]');
    var stepEls = root.querySelectorAll('[data-step]');
    var backBtn = root.querySelector('[data-rc-back]');
    var resultPanel = root.querySelector('[data-rc-result]');
    var resultPrice = root.querySelector('[data-rc-result-price]');
    var wizard = root.querySelector('[data-rc-wizard]');
    var contactPanel = root.querySelector('[data-rc-contact]');
    var messageField = root.querySelector('[data-rc-message-field]');
    var requestQuoteBtn = root.querySelector('[data-rc-request-quote]');
    var restartBtn = root.querySelector('[data-rc-restart]');

    var lastSummary = '';

    // Dawn/Sense's "floating" field label relies on CSS's
    // `:not(:placeholder-shown)` selector. That doesn't reliably re-evaluate
    // in every browser (Safari in particular) when a <textarea> value is set
    // via script rather than typed by the visitor — the label then stays
    // large and overlaps the text already entered. `.rc-is-filled` is a
    // manually-driven fallback class for the same visual effect.
    function syncMessageFieldFilledState() {
      if (!messageField) return;
      messageField.classList.toggle('rc-is-filled', messageField.value.trim().length > 0);
    }

    if (messageField) {
      messageField.addEventListener('input', syncMessageFieldFilledState);
    }

    function showStep(n) {
      state.step = n;
      stepEls.forEach(function (el) {
        el.hidden = parseInt(el.getAttribute('data-step'), 10) !== n;
      });
      progressEls.forEach(function (el) {
        var i = parseInt(el.getAttribute('data-step-indicator'), 10);
        el.classList.toggle('is-active', i === n);
        el.classList.toggle('is-done', i < n);
      });
      if (backBtn) backBtn.hidden = n === 1;
      root.querySelector('[data-rc-wizard]').hidden = false;
      if (resultPanel) resultPanel.hidden = true;
    }

    function goToStep(n) {
      if (n < 1) n = 1;
      if (n > STEP_COUNT) {
        showResult();
        return;
      }
      showStep(n);
    }

    function updateBranchVisibility() {
      var categorySection = root.querySelector('[data-rc-category-section]');
      if (categorySection) categorySection.hidden = !state.branch;
      root.querySelectorAll('[data-branch-group]').forEach(function (el) {
        el.hidden = el.getAttribute('data-branch-group') !== state.branch;
      });
    }

    function showResult() {
      var estimate = computeEstimate(state);
      if (!estimate) return;
      lastSummary = formatSummary(state, estimate);

      if (resultPrice) {
        resultPrice.textContent = '€' + estimate.low + '–' + estimate.high;
      }
      if (wizard) wizard.hidden = true;
      if (resultPanel) resultPanel.hidden = false;
      if (contactPanel) contactPanel.hidden = true;

      if (messageField) {
        // Write the summary into the message field, but don't overwrite it
        // if the visitor already added their own text and comes back.
        messageField.value = lastSummary;
        syncMessageFieldFilledState();
      }
    }

    function ensureSummaryPresent() {
      if (!messageField) return;
      if (messageField.value.indexOf(RC_SUMMARY_STRINGS.marker) === -1) {
        messageField.value = lastSummary + '\n\n' + messageField.value;
        syncMessageFieldFilledState();
      }
    }

    // Step 1: branch + category selection
    root.querySelectorAll('[data-select-branch]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.branch = btn.getAttribute('data-select-branch');
        root.querySelectorAll('[data-select-branch]').forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        updateBranchVisibility();
      });
    });

    root.querySelectorAll('[data-select-category]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.category = btn.getAttribute('data-select-category');
        root.querySelectorAll('[data-select-category]').forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        renderStepTwo();
        goToStep(2);
      });
    });

    // Step 2: work type — content generated based on the branch
    function renderStepTwo() {
      root.querySelectorAll('[data-worktype-group]').forEach(function (el) {
        el.hidden = el.getAttribute('data-worktype-group') !== state.branch;
      });
    }

    root.querySelectorAll('[data-select-worktype]').forEach(function (input) {
      input.addEventListener('change', function () {
        state.workType = input.value;
        goToStep(3);
      });
    });

    // Step 3: size
    root.querySelectorAll('[data-select-size]').forEach(function (input) {
      input.addEventListener('change', function () {
        state.size = input.value;
        renderStepFour();
        goToStep(4);
      });
    });

    // Step 4: material — content based on the branch
    function renderStepFour() {
      root.querySelectorAll('[data-material-group]').forEach(function (el) {
        el.hidden = el.getAttribute('data-material-group') !== state.branch;
      });
    }

    root.querySelectorAll('[data-select-material]').forEach(function (input) {
      input.addEventListener('change', function () {
        state.material = input.value;
        renderStepFive();
        goToStep(5);
      });
    });

    // Step 5: transport — fee hints depend on the size chosen in step 3
    function renderStepFive() {
      var sizeTransportFees = RC_TRANSPORT_FEE[state.size];
      root.querySelectorAll('[data-transport-fee]').forEach(function (el) {
        var key = el.getAttribute('data-transport-fee');
        var fee = sizeTransportFees && sizeTransportFees[key];
        el.textContent = fee ? '€' + fee : '';
      });
    }

    root.querySelectorAll('[data-select-transport]').forEach(function (input) {
      input.addEventListener('change', function () {
        state.transport = input.value;
        goToStep(6); // triggers showResult()
      });
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        goToStep(state.step - 1);
      });
    }

    if (requestQuoteBtn) {
      requestQuoteBtn.addEventListener('click', function () {
        if (contactPanel) {
          contactPanel.hidden = false;
          contactPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    if (restartBtn) {
      restartBtn.addEventListener('click', function () {
        state.step = 1;
        state.branch = null;
        state.category = null;
        state.workType = null;
        state.size = null;
        state.material = null;
        state.transport = null;
        root.querySelectorAll('.is-selected').forEach(function (el) {
          el.classList.remove('is-selected');
        });
        root.querySelectorAll('input[type="radio"]').forEach(function (el) {
          el.checked = false;
        });
        if (contactPanel) contactPanel.hidden = true;
        updateBranchVisibility();
        goToStep(1);
      });
    }

    var contactForm = root.querySelector('#RCContactForm');
    if (contactForm) {
      contactForm.addEventListener('submit', ensureSummaryPresent);
    }

    // Reset to initial state
    updateBranchVisibility();
    showStep(1);
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-rc-calculator]').forEach(initCalculator);
  });

  // Expose for manual testing / debugging in the browser console.
  window.RC_PRICING = RC_PRICING;
  window.RC_computeEstimate = computeEstimate;
})();
